import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2, FiSave } from 'react-icons/fi';
import api from '../../services/api';

const EMPTY_CONTAINER = {
  container_no: '',
  tp_sz: '',
  seal_no: '',
  pick_up_no: '',
  pkg_type: 'CARTON(S)',
  pkg_count: '',
  weight_kg: '',
  weight_lb: '',
  measurement_cbm: '',
  measurement_cft: '',
  lfd: '',
  appt: '',
  pick_up_date: '',
  empty_return_date: '',
  po_no: '',
  storage_start_date: '',
  storage_end_date: '',
};

const TP_SZ_OPTIONS = ['20GP', '20HC', '40GP', '40HC', '40HQ', '45HC', '20OT', '40OT', '20FR', '40FR', '20RF', '40RF'];
const PKG_OPTIONS = ['CARTON(S)', 'PALLET(S)', 'BOX(ES)', 'CASE(S)', 'PIECE(S)', 'PACKAGE(S)', 'DRUM(S)', 'ROLL(S)'];

const SHIPMENT_TYPE_ENDPOINTS = {
  truck: 'truck',
  'ocean-import': 'ocean-import',
  'ocean-export': 'ocean-export',
  'air-import': 'air-import',
  misc: 'misc',
};

const ContainerTab = ({ shipmentId, shipmentType = 'truck' }) => {
  const [containers, setContainers] = useState([]);
  const [houses, setHouses] = useState([]);
  const [mblNo, setMblNo] = useState('');
  const [scope, setScope] = useState('');
  const [commodities, setCommodities] = useState([]);
  const [instruction, setInstruction] = useState('');
  const [description, setDescription] = useState('');
  const [poNo, setPoNo] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedContainers, setSelectedContainers] = useState(new Set());
  const [selectedCommodities, setSelectedCommodities] = useState(new Set());
  const [weightUnit, setWeightUnit] = useState('KG');
  const [measurementUnit, setMeasurementUnit] = useState('CBM');

  // Track new containers/commodities not yet saved to server
  const [newContainerIds, setNewContainerIds] = useState(new Set());
  const [newCommodityIds, setNewCommodityIds] = useState(new Set());
  const [deletedContainerIds, setDeletedContainerIds] = useState(new Set());
  const [deletedCommodityIds, setDeletedCommodityIds] = useState(new Set());

  // Snapshot of server data for dirty checking
  const serverDataRef = useRef({ containers: [], commodities: [], instruction: '', description: '' });
  let nextTempId = useRef(-1);

  const endpoint = SHIPMENT_TYPE_ENDPOINTS[shipmentType] || 'truck';

  const loadData = useCallback(async () => {
    if (!shipmentId) return;
    try {
      // shipment_type explicitly, never left to the Referer fallback: the
      // numeric id repeats across modules, and a blank type resolves to
      // 'truck'. Truck TK-20260159 once listed ocean import OI-26080191's
      // charges for exactly this reason, and the container table has the same
      // shape of bug — it would show another module's boxes.
      const scope = { params: { shipment_type: shipmentType } };
      const [contRes, commRes, shipRes] = await Promise.all([
        api.get(`/containers/shipment/${shipmentId}`, scope),
        api.get(`/containers/shipment/${shipmentId}/commodities`, scope),
        api.get(`/${endpoint}/${shipmentId}`),
      ]);
      setContainers(contRes.data);
      setCommodities(commRes.data);
      const hs = (shipRes.data?.hbls || shipRes.data?.hawbs || [])
        .filter(h => (h.hbl_no || h.hawb_no || '').trim());
      setHouses(hs);
      setMblNo(shipRes.data?.mbl_no || shipRes.data?.mawb_no || '');
      setScope(prev => (prev || (hs.length ? String(hs[0].id) : '')));
      const desc = shipRes.data?.description || '';
      const instr = shipRes.data?.instruction || '';
      const po = shipRes.data?.po_no || '';
      setDescription(desc);
      setInstruction(instr);
      setPoNo(po);
      // Store server snapshot
      serverDataRef.current = {
        containers: JSON.parse(JSON.stringify(contRes.data)),
        commodities: JSON.parse(JSON.stringify(commRes.data)),
        instruction: instr,
        description: desc,
        poNo: po,
      };
      setHasChanges(false);
      setNewContainerIds(new Set());
      setNewCommodityIds(new Set());
      setDeletedContainerIds(new Set());
      setDeletedCommodityIds(new Set());
    } catch (error) {
      console.error('Failed to load container data:', error);
    } finally {
      setLoading(false);
    }
  }, [shipmentId, endpoint]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Mark dirty
  const markDirty = () => setHasChanges(true);

  // Container actions (local only)
  const addContainer = () => {
    const tempId = nextTempId.current--;
    const newCont = { ...EMPTY_CONTAINER, id: tempId, sort_order: containers.length + 1 };
    setContainers(prev => [...prev, newCont]);
    setNewContainerIds(prev => new Set([...prev, tempId]));
    markDirty();
  };

  const addBulkContainers = (count = 5) => {
    const newConts = [];
    const newIds = new Set(newContainerIds);
    for (let i = 0; i < count; i++) {
      const tempId = nextTempId.current--;
      newConts.push({ ...EMPTY_CONTAINER, id: tempId, sort_order: containers.length + i + 1 });
      newIds.add(tempId);
    }
    setContainers(prev => [...prev, ...newConts]);
    setNewContainerIds(newIds);
    markDirty();
  };

  const updateContainer = (id, field, value) => {
    setContainers(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
    markDirty();
  };

  const deleteSelectedContainers = () => {
    if (selectedContainers.size === 0) return;
    // Track server containers being deleted
    const serverDeletions = new Set(deletedContainerIds);
    selectedContainers.forEach(id => {
      if (!newContainerIds.has(id)) {
        serverDeletions.add(id);
      }
    });
    // Remove new container ids that are being deleted
    const updatedNewIds = new Set(newContainerIds);
    selectedContainers.forEach(id => updatedNewIds.delete(id));

    setDeletedContainerIds(serverDeletions);
    setNewContainerIds(updatedNewIds);
    setContainers(prev => prev.filter(c => !selectedContainers.has(c.id)));
    setSelectedContainers(new Set());
    markDirty();
  };

  // Commodity actions (local only)
  const addCommodity = () => {
    const tempId = nextTempId.current--;
    const newComm = { id: tempId, commodity_description: '', hts_code: '', container_id: '' };
    setCommodities(prev => [...prev, newComm]);
    setNewCommodityIds(prev => new Set([...prev, tempId]));
    markDirty();
  };

  const updateCommodity = (id, field, value) => {
    setCommodities(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
    markDirty();
  };

  const deleteSelectedCommodities = () => {
    if (selectedCommodities.size === 0) return;
    const serverDeletions = new Set(deletedCommodityIds);
    selectedCommodities.forEach(id => {
      if (!newCommodityIds.has(id)) {
        serverDeletions.add(id);
      }
    });
    const updatedNewIds = new Set(newCommodityIds);
    selectedCommodities.forEach(id => updatedNewIds.delete(id));

    setDeletedCommodityIds(serverDeletions);
    setNewCommodityIds(updatedNewIds);
    setCommodities(prev => prev.filter(c => !selectedCommodities.has(c.id)));
    setSelectedCommodities(new Set());
    markDirty();
  };

  // ========== SAVE ALL ==========
  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. Delete removed containers from server
      if (deletedContainerIds.size > 0) {
        await Promise.all([...deletedContainerIds].map(id => api.delete(`/containers/${id}`)));
      }

      // 2. Delete removed commodities from server
      if (deletedCommodityIds.size > 0) {
        await Promise.all([...deletedCommodityIds].map(id => api.delete(`/containers/commodities/${id}`)));
      }

      // 3. Create new containers on server
      for (const cont of containers) {
        if (newContainerIds.has(cont.id)) {
          const { id, ...data } = cont;
          await api.post(`/containers/shipment/${shipmentId}`, { ...data, shipment_type: shipmentType });
        }
      }

      // 4. Update existing containers that changed
      const serverMap = {};
      serverDataRef.current.containers.forEach(c => { serverMap[c.id] = c; });
      for (const cont of containers) {
        if (newContainerIds.has(cont.id) || deletedContainerIds.has(cont.id)) continue;
        const orig = serverMap[cont.id];
        if (!orig) continue;
        const updates = {};
        // hbl_id is not one of EMPTY_CONTAINER's typed-in fields but it is
        // edited here, so it is diffed explicitly or an assignment is saved
        // nowhere.
        if (String(cont.hbl_id || '') !== String(orig.hbl_id || '')) {
          updates.hbl_id = cont.hbl_id || null;
        }
        Object.keys(EMPTY_CONTAINER).forEach(key => {
          const newVal = cont[key] || '';
          const origVal = orig[key] || '';
          if (String(newVal) !== String(origVal)) {
            updates[key] = cont[key];
          }
        });
        if (Object.keys(updates).length > 0) {
          await api.put(`/containers/${cont.id}`, updates);
        }
      }

      // 5. Create new commodities on server
      for (const comm of commodities) {
        if (newCommodityIds.has(comm.id)) {
          const { id, ...data } = comm;
          await api.post(`/containers/shipment/${shipmentId}/commodities`, data);
        }
      }

      // 6. Update existing commodities that changed
      const commServerMap = {};
      serverDataRef.current.commodities.forEach(c => { commServerMap[c.id] = c; });
      for (const comm of commodities) {
        if (newCommodityIds.has(comm.id) || deletedCommodityIds.has(comm.id)) continue;
        const orig = commServerMap[comm.id];
        if (!orig) continue;
        const updates = {};
        ['commodity_description', 'hts_code', 'container_id'].forEach(key => {
          if (String(comm[key] || '') !== String(orig[key] || '')) {
            updates[key] = comm[key];
          }
        });
        if (Object.keys(updates).length > 0) {
          await api.put(`/containers/commodities/${comm.id}`, updates);
        }
      }

      // 7. Save instruction, description & P.O. No.
      const shipmentUpdates = {};
      if (instruction !== serverDataRef.current.instruction) shipmentUpdates.instruction = instruction;
      if (description !== serverDataRef.current.description) shipmentUpdates.description = description;
      if (poNo !== serverDataRef.current.poNo) shipmentUpdates.po_no = poNo;
      if (Object.keys(shipmentUpdates).length > 0) {
        await api.put(`/${endpoint}/${shipmentId}`, shipmentUpdates);
      }

      toast.success('Container data saved');
      // Reload to get fresh server data
      await loadData();
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save container data');
    } finally {
      setSaving(false);
    }
  };

  // Totals
  const totals = containers.reduce(
    (acc, c) => ({
      pkg_count: acc.pkg_count + (Number(c.pkg_count) || 0),
      weight_kg: acc.weight_kg + (Number(c.weight_kg) || 0),
      weight_lb: acc.weight_lb + (Number(c.weight_lb) || 0),
      measurement_cbm: acc.measurement_cbm + (Number(c.measurement_cbm) || 0),
      measurement_cft: acc.measurement_cft + (Number(c.measurement_cft) || 0),
    }),
    { pkg_count: 0, weight_kg: 0, weight_lb: 0, measurement_cbm: 0, measurement_cft: 0 }
  );

  const houseNo = (h) => (h?.hbl_no || h?.hawb_no || '').trim();
  const scoped = houses.length > 0;
  const currentHouse = houses.find(h => String(h.id) === String(scope));

  const sumOf = (rows) => rows.reduce(
    (a, c) => ({
      pkg_count: a.pkg_count + (Number(c.pkg_count) || 0),
      weight_kg: a.weight_kg + (Number(c.weight_kg) || 0),
      weight_lb: a.weight_lb + (Number(c.weight_lb) || 0),
      measurement_cbm: a.measurement_cbm + (Number(c.measurement_cbm) || 0),
      measurement_cft: a.measurement_cft + (Number(c.measurement_cft) || 0),
    }),
    { pkg_count: 0, weight_kg: 0, weight_lb: 0, measurement_cbm: 0, measurement_cft: 0 });

  const boxesOf = (h) => containers.filter(
    c => String(c.hbl_id || '') === String(h.id));
  const unassigned = containers.filter(c => !String(c.hbl_id || '').trim());

  const toggleContainerSelect = (id) => {
    setSelectedContainers(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllContainers = () => {
    if (selectedContainers.size === containers.length) {
      setSelectedContainers(new Set());
    } else {
      setSelectedContainers(new Set(containers.map(c => c.id)));
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>;
  }

  if (!shipmentId) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
        <p>Please save the shipment first before adding containers.</p>
      </div>
    );
  }

  const TINTS = ['#f59e0b', '#3b82f6', '#f97316', '#10b981', '#ef4444',
                 '#8b5cf6', '#0ea5e9', '#84cc16'];

  const num = (v, unit) =>
    `${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${unit}`;

  const houseSidebar = scoped && (
    <aside style={{
      width: 236, flex: '0 0 236px', padding: 12,
      borderLeft: '1px solid #e2e8f0', background: '#f8fafc', alignSelf: 'stretch',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b',
                    textTransform: 'uppercase', letterSpacing: '.04em',
                    marginBottom: 8 }}>
        House B/L ({houses.length})
      </div>
      {houses.map((h, i) => {
        const mine = boxesOf(h);
        const t = sumOf(mine);
        const tint = TINTS[i % TINTS.length];
        const active = String(scope) === String(h.id);
        return (
          <button key={h.id} type="button" onClick={() => setScope(String(h.id))}
            style={{
              display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
              padding: 0, marginBottom: 8, borderRadius: 8, font: 'inherit',
              border: active ? `2px solid ${tint}` : '1px solid #e2e8f0',
              overflow: 'hidden', background: '#fff',
            }}>
            <div style={{ background: tint, color: '#fff', padding: '6px 10px',
                          fontSize: 13, fontWeight: 700 }}>
              {houseNo(h)}
            </div>
            {(h.consignee || h.customer) && (
              <div style={{ padding: '6px 10px 0', fontSize: 11, color: '#475569' }}>
                {h.consignee || h.customer}
              </div>
            )}
            <div style={{ padding: '6px 10px 8px', fontSize: 11, color: '#334155',
                          display: 'grid', gap: 2 }}>
              <span>{mine.length} {mine.length === 1 ? 'container' : 'containers'}</span>
              <span>PKG: {t.pkg_count.toLocaleString()}</span>
              <span>Weight: {num(t.weight_kg, 'KG')}</span>
              <span>Measurement: {num(t.measurement_cbm, 'CBM')}</span>
            </div>
          </button>
        );
      })}

      {unassigned.length > 0 && (
        <div style={{
          marginTop: 12, padding: '8px 10px', borderRadius: 8,
          background: '#fef2f2', border: '1px solid #fecaca',
          fontSize: 11, color: '#b91c1c', lineHeight: 1.5,
        }}>
          <b>{unassigned.length} container{unassigned.length === 1 ? '' : 's'} unassigned.</b>
          {' '}A delivery order cannot be scoped to a house until every box on
          the shipment names one — set the HB/L column above and save.
        </div>
      )}
    </aside>
  );

  const housePanel = scoped && currentHouse && (() => {
    const mine = boxesOf(currentHouse);
    const t = sumOf(mine);
    const tint = TINTS[houses.findIndex(h => String(h.id) === String(scope))
                        % TINTS.length];
    return (
      <div className="container-section" style={{ marginTop: 14 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '9px 14px', background: tint, color: '#fff',
        }}>
          <span style={{ fontSize: 12, opacity: .85 }}>HB/L</span>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{houseNo(currentHouse)}</span>
          {(currentHouse.consignee || currentHouse.customer) && (
            <span style={{ fontSize: 12, opacity: .9 }}>
              CUSTOMER : {currentHouse.consignee || currentHouse.customer}
            </span>
          )}
          {currentHouse.op && (
            <span style={{ fontSize: 12, opacity: .9 }}>OP : {currentHouse.op}</span>
          )}
        </div>
        <div className="container-table-wrapper">
          <table className="container-table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th className="col-container-no">Container No.</th>
                <th className="col-tpsz">TP/SZ</th>
                <th className="col-pkg">PKG</th>
                <th className="col-weight">Weight</th>
                <th className="col-measurement">Measurement</th>
                <th>P.O. No.</th>
              </tr>
            </thead>
            <tbody>
              {mine.length === 0 ? (
                <tr><td colSpan="7" className="empty-row">
                  No container is assigned to this house bill yet.
                </td></tr>
              ) : mine.map((c, i) => (
                <tr key={c.id}>
                  <td className="col-num">{i + 1}</td>
                  <td>{c.container_no || ''}</td>
                  <td>{c.tp_sz || ''}</td>
                  <td>{c.pkg_count || ''}</td>
                  <td>{c.weight_kg ? num(c.weight_kg, 'KG') : ''}</td>
                  <td>{c.measurement_cbm ? num(c.measurement_cbm, 'CBM') : ''}</td>
                  <td>{c.po_no || ''}</td>
                </tr>
              ))}
            </tbody>
            {mine.length > 0 && (
              <tfoot>
                <tr className="totals-row">
                  <td colSpan="3" className="totals-label">Total</td>
                  <td className="totals-value">{t.pkg_count.toLocaleString()}</td>
                  <td className="totals-value">{num(t.weight_kg, 'KG')}</td>
                  <td className="totals-value">{num(t.measurement_cbm, 'CBM')}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  })();

  return (
    <div className="container-tab"
         style={scoped ? { display: 'flex', alignItems: 'flex-start' } : undefined}>
      <div style={scoped ? { flex: 1, minWidth: 0 } : undefined}>
      {/* Save Button Bar */}
      <div className="container-save-bar">
        <button
          className={`btn-save-containers ${hasChanges ? 'has-changes' : ''}`}
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          <FiSave size={14} />
          {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
        </button>
      </div>

      {/* P.O. No Section */}
      <div className="container-section">
        <div className="po-section">
          <div className="po-header">
            <span className="po-title">P.O. No.</span>
            <span className="po-hint">Please list down P.O. No. for this MB/L.</span>
          </div>
          <input
            type="text"
            className="po-input"
            value={poNo}
            onChange={(e) => { setPoNo(e.target.value); markDirty(); }}
            placeholder="Add P.O. here..."
          />
        </div>
      </div>

      {/* Container List */}
      <div className="container-section">
        <div className="container-list-header">
          <span className="section-title">Container List</span>
          {scoped && mblNo && (
            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>
              MB/L {mblNo} — every box on the shipment, and which house each
              belongs to
            </span>
          )}
          <div className="container-actions">
            <button className="btn-add-container" onClick={addContainer} title="Add container">
              <FiPlus />
            </button>
            <button className="btn-add-bulk" onClick={() => addBulkContainers(5)}>
              +5
            </button>
            {selectedContainers.size > 0 && (
              <button className="btn-delete-container" onClick={deleteSelectedContainers} title="Delete selected">
                <FiTrash2 />
              </button>
            )}
          </div>
        </div>

        <div className="container-table-wrapper">
          <table className="container-table">
            <thead>
              <tr>
                <th className="col-check">
                  <input
                    type="checkbox"
                    checked={containers.length > 0 && selectedContainers.size === containers.length}
                    onChange={toggleAllContainers}
                  />
                </th>
                <th className="col-num">#</th>
                {scoped && <th style={{ minWidth: 130 }}>HB/L</th>}
                <th className="col-container-no">Container No.</th>
                <th className="col-tpsz">TP/SZ</th>
                <th className="col-seal">Seal No.</th>
                <th className="col-pickup-no">Pick Up No.</th>
                <th className="col-pkg">PKG</th>
                <th className="col-weight">
                  <div className="th-with-select">
                    Weight
                    <select className="th-unit-select" value={weightUnit} onChange={(e) => setWeightUnit(e.target.value)}>
                      <option value="KG">KG</option>
                      <option value="LB">LB</option>
                    </select>
                  </div>
                </th>
                <th className="col-measurement">
                  <div className="th-with-select">
                    Measurement
                    <select className="th-unit-select" value={measurementUnit} onChange={(e) => setMeasurementUnit(e.target.value)}>
                      <option value="CBM">CBM</option>
                      <option value="CFT">CFT</option>
                    </select>
                  </div>
                </th>
                <th className="col-lfd">LFD</th>
                <th className="col-appt">Appt.</th>
                <th className="col-pickup">Pick Up</th>
                <th className="col-return">Empty Return</th>
                <th className="col-po">P.O. No.</th>
              </tr>
            </thead>
            <tbody>
              {containers.length === 0 ? (
                <tr>
                  <td colSpan={scoped ? 15 : 14} className="empty-row">
                    No containers. Click <strong>+</strong> to add one.
                  </td>
                </tr>
              ) : (
                containers.map((c, idx) => (
                  <React.Fragment key={c.id}>
                    <tr className={selectedContainers.has(c.id) ? 'row-selected' : ''}>
                      <td className="col-check">
                        <input
                          type="checkbox"
                          checked={selectedContainers.has(c.id)}
                          onChange={() => toggleContainerSelect(c.id)}
                        />
                      </td>
                      <td className="col-num">{idx + 1}</td>
                      {scoped && (
                        <td>
                          {/* Which house this box belongs to. Nothing in the
                              app could set this before, so a delivery order
                              for one house of a multi-house shipment simply
                              refused to render. Unassigned is shown in red
                              because that is the state that breaks it. */}
                          <select
                            className="cell-select"
                            style={!String(c.hbl_id || '').trim()
                              ? { color: '#b91c1c', fontWeight: 600 } : undefined}
                            value={c.hbl_id || ''}
                            onChange={(e) => updateContainer(c.id, 'hbl_id',
                              e.target.value ? Number(e.target.value) : null)}
                          >
                            <option value="">— unassigned —</option>
                            {houses.map(h => (
                              <option key={h.id} value={h.id}>{houseNo(h)}</option>
                            ))}
                          </select>
                        </td>
                      )}
                      <td>
                        <input
                          type="text"
                          className="cell-input cell-wide"
                          value={c.container_no || ''}
                          onChange={(e) => updateContainer(c.id, 'container_no', e.target.value)}
                          placeholder="e.g. OOCU7626039"
                        />
                      </td>
                      <td>
                        <select
                          className="cell-select"
                          value={c.tp_sz || ''}
                          onChange={(e) => updateContainer(c.id, 'tp_sz', e.target.value)}
                        >
                          <option value="">--</option>
                          {TP_SZ_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="cell-input"
                          value={c.seal_no || ''}
                          onChange={(e) => updateContainer(c.id, 'seal_no', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="cell-input"
                          value={c.pick_up_no || ''}
                          onChange={(e) => updateContainer(c.id, 'pick_up_no', e.target.value)}
                        />
                      </td>
                      <td>
                        <div className="cell-pkg">
                          <select
                            className="cell-select-sm"
                            value={c.pkg_type || 'CARTON(S)'}
                            onChange={(e) => updateContainer(c.id, 'pkg_type', e.target.value)}
                          >
                            {PKG_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                          <input
                            type="number"
                            className="cell-input-sm"
                            value={c.pkg_count || ''}
                            onChange={(e) => updateContainer(c.id, 'pkg_count', e.target.value)}
                            placeholder="0"
                          />
                        </div>
                      </td>
                      <td>
                        <div className="cell-weight">
                          <input
                            type="number"
                            step="0.01"
                            className="cell-input-sm"
                            value={weightUnit === 'KG' ? (c.weight_kg || '') : (c.weight_lb || '')}
                            onChange={(e) => updateContainer(c.id, weightUnit === 'KG' ? 'weight_kg' : 'weight_lb', e.target.value)}
                            placeholder="0"
                          />
                          <span className="cell-unit">{weightUnit}</span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-weight">
                          <input
                            type="number"
                            step="0.01"
                            className="cell-input-sm"
                            value={measurementUnit === 'CBM' ? (c.measurement_cbm || '') : (c.measurement_cft || '')}
                            onChange={(e) => updateContainer(c.id, measurementUnit === 'CBM' ? 'measurement_cbm' : 'measurement_cft', e.target.value)}
                            placeholder="0"
                          />
                          <span className="cell-unit">{measurementUnit}</span>
                        </div>
                      </td>
                      <td>
                        <input
                          type="date"
                          className="cell-date"
                          value={c.lfd ? c.lfd.split('T')[0] : ''}
                          onChange={(e) => updateContainer(c.id, 'lfd', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          className="cell-date"
                          value={c.appt ? c.appt.split('T')[0] : ''}
                          onChange={(e) => updateContainer(c.id, 'appt', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          className="cell-date"
                          value={c.pick_up_date ? c.pick_up_date.split('T')[0] : ''}
                          onChange={(e) => updateContainer(c.id, 'pick_up_date', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          className="cell-date"
                          value={c.empty_return_date ? c.empty_return_date.split('T')[0] : ''}
                          onChange={(e) => updateContainer(c.id, 'empty_return_date', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="cell-input"
                          value={c.po_no || ''}
                          onChange={(e) => updateContainer(c.id, 'po_no', e.target.value)}
                        />
                      </td>
                    </tr>
                    {/* Storage dates row */}
                    <tr className="storage-row">
                      <td colSpan={scoped ? 4 : 3}></td>
                      <td colSpan="2">
                        <div className="storage-field">
                          <span className="storage-label">Storage Start</span>
                          <input
                            type="date"
                            className="cell-date"
                            value={c.storage_start_date ? c.storage_start_date.split('T')[0] : ''}
                            onChange={(e) => updateContainer(c.id, 'storage_start_date', e.target.value)}
                          />
                        </div>
                      </td>
                      <td colSpan="2">
                        <div className="storage-field">
                          <span className="storage-label">Storage End</span>
                          <input
                            type="date"
                            className="cell-date"
                            value={c.storage_end_date ? c.storage_end_date.split('T')[0] : ''}
                            onChange={(e) => updateContainer(c.id, 'storage_end_date', e.target.value)}
                          />
                        </div>
                      </td>
                      <td colSpan="7"></td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
            {containers.length > 0 && (
              <tfoot>
                <tr className="totals-row">
                  <td colSpan={scoped ? 7 : 6} className="totals-label">Container Total</td>
                  <td className="totals-value">{totals.pkg_count.toLocaleString()}</td>
                  <td className="totals-value">
                    {weightUnit === 'KG'
                      ? `${totals.weight_kg.toLocaleString(undefined, { minimumFractionDigits: 2 })} KG`
                      : `${totals.weight_lb.toLocaleString(undefined, { minimumFractionDigits: 2 })} LB`
                    }
                  </td>
                  <td className="totals-value">
                    {measurementUnit === 'CBM'
                      ? `${totals.measurement_cbm.toLocaleString(undefined, { minimumFractionDigits: 2 })} CBM`
                      : `${totals.measurement_cft.toLocaleString(undefined, { minimumFractionDigits: 2 })} CFT`
                    }
                  </td>
                  <td colSpan="5"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Commodity Section */}
      <div className="container-section">
        <div className="container-list-header">
          <span className="section-title">Commodity</span>
          <div className="container-actions">
            <button className="btn-add-container" onClick={addCommodity} title="Add commodity">
              <FiPlus />
            </button>
            {selectedCommodities.size > 0 && (
              <button className="btn-delete-container" onClick={deleteSelectedCommodities} title="Delete selected">
                <FiTrash2 />
              </button>
            )}
          </div>
        </div>

        <table className="container-table commodity-table">
          <thead>
            <tr>
              <th className="col-check">
                <input
                  type="checkbox"
                  checked={commodities.length > 0 && selectedCommodities.size === commodities.length}
                  onChange={() => {
                    if (selectedCommodities.size === commodities.length) {
                      setSelectedCommodities(new Set());
                    } else {
                      setSelectedCommodities(new Set(commodities.map(c => c.id)));
                    }
                  }}
                />
              </th>
              <th>Commodity Description</th>
              <th>HTS Code</th>
              <th>Container</th>
            </tr>
          </thead>
          <tbody>
            {commodities.length === 0 ? (
              <tr>
                <td colSpan="4" className="empty-row">
                  No commodities. Click <strong>+</strong> to add.
                </td>
              </tr>
            ) : (
              commodities.map(comm => (
                <tr key={comm.id} className={selectedCommodities.has(comm.id) ? 'row-selected' : ''}>
                  <td className="col-check">
                    <input
                      type="checkbox"
                      checked={selectedCommodities.has(comm.id)}
                      onChange={() => {
                        setSelectedCommodities(prev => {
                          const next = new Set(prev);
                          next.has(comm.id) ? next.delete(comm.id) : next.add(comm.id);
                          return next;
                        });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="cell-input cell-wide"
                      value={comm.commodity_description || ''}
                      onChange={(e) => updateCommodity(comm.id, 'commodity_description', e.target.value)}
                      placeholder="e.g. AIR FRESHENER"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="cell-input"
                      value={comm.hts_code || ''}
                      onChange={(e) => updateCommodity(comm.id, 'hts_code', e.target.value)}
                      placeholder="e.g. 3307.49"
                    />
                  </td>
                  <td>
                    <select
                      className="cell-select"
                      value={comm.container_id || ''}
                      onChange={(e) => updateCommodity(comm.id, 'container_id', e.target.value)}
                    >
                      <option value="">--</option>
                      {containers.filter(c => c.id > 0).map(c => (
                        <option key={c.id} value={c.id}>{c.container_no || `Container #${c.sort_order}`}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Instruction & Description */}
      <div className="container-section">
        <div className="instruction-grid">
          <div className="instruction-block">
            <div className="form-section-header">Instruction</div>
            <textarea
              className="shipment-memo"
              value={instruction}
              onChange={(e) => { setInstruction(e.target.value); markDirty(); }}
              placeholder="Add instruction..."
            />
          </div>
          <div className="instruction-block">
            <div className="form-section-header">Description</div>
            <textarea
              className="shipment-memo"
              value={description}
              onChange={(e) => { setDescription(e.target.value); markDirty(); }}
              placeholder="Add description..."
            />
          </div>
        </div>
      </div>

      {housePanel}

      {/* Bottom Save Button */}
      <div className="container-save-bar bottom">
        <button
          className={`btn-save-containers ${hasChanges ? 'has-changes' : ''}`}
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          <FiSave size={14} />
          {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
        </button>
      </div>
      </div>
      {houseSidebar}
    </div>
  );
};

export default ContainerTab;
